import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { customCategory, defaultEmojiPickerSize, frequentCategory, searchCategory, userCustomTitle, yourUploadsCategory } from '../../util/constants';
import { CategoryDescriptionMap } from './categories';
import CategoryTracker from './CategoryTracker';
import { sizes } from './EmojiPickerSizes';
import { CategoryHeadingItem, EmojisRowItem, LoadingItem, virtualItemRenderer } from './EmojiPickerVirtualItems';
import EmojiActions from '../common/EmojiActions';
import { emojiPickerHeightOffset, scrollToRow as _scrollToRow } from './utils';
import { VirtualList } from './VirtualList';
import { EmojiPickerListContextProvider } from '../../context/EmojiPickerListContext';
import EmojiPickerTabPanel from './EmojiPickerTabPanel';

/**
 * Test id for wrapper Emoji Picker List div
 */
export var RENDER_EMOJI_PICKER_LIST_TESTID = 'render-emoji-picker-list';
var categoryClassname = 'emoji-category';

/**
 * Emoji grouped by a category title ie. Frequent, Your Uploads, All Uploads
 */

var byOrder = function byOrder(orderableA, orderableB) {
  return (orderableA.order || 0) - (orderableB.order || 0);
};
export var EmojiPickerVirtualListInternal = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var emojis = props.emojis,
    currentUser = props.currentUser,
    _props$onEmojiSelecte = props.onEmojiSelected,
    onEmojiSelected = _props$onEmojiSelecte === void 0 ? function () {} : _props$onEmojiSelecte,
    _props$onEmojiActive = props.onEmojiActive,
    onEmojiActive = _props$onEmojiActive === void 0 ? function () {} : _props$onEmojiActive,
    _props$onEmojiDelete = props.onEmojiDelete,
    onEmojiDelete = _props$onEmojiDelete === void 0 ? function () {} : _props$onEmojiDelete,
    _props$onCategoryActi = props.onCategoryActivated,
    onCategoryActivated = _props$onCategoryActi === void 0 ? function () {} : _props$onCategoryActi,
    _props$onSearch = props.onSearch,
    onSearch = _props$onSearch === void 0 ? function () {} : _props$onSearch,
    _props$size = props.size,
    size = _props$size === void 0 ? defaultEmojiPickerSize : _props$size,
    query = props.query,
    loading = props.loading,
    selectedTone = props.selectedTone,
    onToneSelected = props.onToneSelected,
    onToneSelectorCancelled = props.onToneSelectorCancelled,
    toneEmoji = props.toneEmoji,
    uploading = props.uploading,
    uploadEnabled = props.uploadEnabled,
    emojiToDelete = props.emojiToDelete,
    initialUploadName = props.initialUploadName,
    uploadErrorMessage = props.uploadErrorMessage,
    onUploadCancelled = props.onUploadCancelled,
    onUploadEmoji = props.onUploadEmoji,
    onDeleteEmoji = props.onDeleteEmoji,
    onCloseDelete = props.onCloseDelete,
    onFileChooserClicked = props.onFileChooserClicked,
    onOpenUpload = props.onOpenUpload,
    activeCategoryId = props.activeCategoryId;
  var listRef = useRef(null);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    allEmojiGroups = _useState2[0],
    setAllEmojiGroups = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    virtualItems = _useState4[0],
    setVirtualItems = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    lastYourUploadsRow = _useState6[0],
    setLastYourUploadsRow = _useState6[1];
  var categoryTracker = useMemo(function () {
    return new CategoryTracker();
  }, []);
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    categoriesChanged = _useState8[0],
    setCategoriesChanged = _useState8[1];
  var groupByCategory = useCallback(function (currentUser) {
    return function (categoryToGroupMap, emoji) {
      addToCategoryMap(categoryToGroupMap, emoji, emoji.category);
      // separate user emojis
      if (emoji.category === customCategory && currentUser && emoji.creatorUserId === currentUser.id) {
        addToCategoryMap(categoryToGroupMap, emoji, yourUploadsCategory);
      }
      return categoryToGroupMap;
    };
  }, []);

  /**
   * onRowsRendered callback function
   *
   * Check the category of top of rendered row and inform category selector to change active category
   * Rove index of emoji picker list
   */
  var onRowsRendered = useCallback(function (indexes) {
    var startIndex = indexes.startIndex;
    var rowItem = virtualItems[startIndex];
    var list = listRef.current;

    // update tabIndex manually, startIndex is not 0 based here
    if (rowItem instanceof CategoryHeadingItem) {
      // if top of row rendered is category heading, update tabIndex for the next emoji row
      list === null || list === void 0 || list.updateFocusIndex(startIndex + 1);
    } else if (rowItem instanceof EmojisRowItem) {
      // if top of row rendered is emoji row, update it's tabIndex.
      list === null || list === void 0 || list.updateFocusIndex(startIndex);
    }
    if (!query) {
      // Calculate category in view - only relevant if categories shown, i.e. no query
      var currentCategory = findCategoryToActivate(rowItem);
      if (currentCategory !== null && activeCategoryId !== currentCategory) {
        if (onCategoryActivated) {
          onCategoryActivated(currentCategory);
        }
      }
    }
  }, [virtualItems, query, activeCategoryId, onCategoryActivated]);
  var buildEmojiGroupedByCategory = useCallback(function (emojis, currentUser) {
    var categoryToGroupMap = emojis.reduce(groupByCategory(currentUser), {});
    setAllEmojiGroups(Object.keys(categoryToGroupMap).map(function (key) {
      return categoryToGroupMap[key];
    }).map(function (group) {
      if (group.category !== frequentCategory) {
        group.emojis.sort(byOrder);
      }
      return group;
    }).sort(byOrder));
  }, [groupByCategory]);
  var addToCategoryMap = function addToCategoryMap(categoryToGroupMap, emoji, category) {
    if (!categoryToGroupMap[category]) {
      var categoryDefinition = CategoryDescriptionMap[category];
      categoryToGroupMap[category] = {
        emojis: [],
        title: categoryDefinition.name,
        category: category,
        order: categoryDefinition.order
      };
    }
    categoryToGroupMap[category].emojis.push(emoji);
    return categoryToGroupMap;
  };
  var buildVirtualItemFromGroup = useCallback(function (group) {
    var items = [];
    items.push(new CategoryHeadingItem({
      id: group.category,
      title: group.title,
      className: categoryClassname
    }));
    var remainingEmojis = group.emojis;
    while (remainingEmojis.length > 0) {
      var rowEmojis = remainingEmojis.slice(0, sizes.emojiPerRow);
      remainingEmojis = remainingEmojis.slice(sizes.emojiPerRow);
      items.push(new EmojisRowItem({
        category: group.category,
        emojis: rowEmojis,
        title: group.title,
        showDelete: group.title === userCustomTitle,
        onSelected: onEmojiSelected,
        onDelete: onEmojiDelete,
        onMouseMove: onEmojiActive,
        onFocus: onEmojiActive
      }));
    }
    return items;
  }, [onEmojiSelected, onEmojiDelete, onEmojiActive]);
  var buildVirtualItems = useCallback(function () {
    var items = [];
    var prevFirstCategory = categoryTracker.getFirstCategory();
    categoryTracker.reset();
    if (loading) {
      items.push(new LoadingItem());
    } else {
      if (query) {
        var search = CategoryDescriptionMap.SEARCH;
        // Only a single "result" category
        items = [].concat(_toConsumableArray(items), _toConsumableArray(buildVirtualItemFromGroup({
          category: searchCategory,
          title: search.name,
          emojis: emojis,
          order: search.order
        })));
      } else {
        // Group by category

        // Not searching show in categories.
        allEmojiGroups.forEach(function (group) {
          // Optimisation - avoid re-rendering unaffected groups for the current selectedShortcut
          // by not passing it to irrelevant groups
          categoryTracker.add(group.emojis[0].category, items.length);
          items = [].concat(_toConsumableArray(items), _toConsumableArray(buildVirtualItemFromGroup(group)));
          if (group.category === yourUploadsCategory) {
            setLastYourUploadsRow(items.length - 1);
          }
        });
      }
    }

    // make sure virtualItems is up-to-date before calling onRowsRendered
    setVirtualItems(items);
    if (!loading && !query) {
      if (categoryTracker.getFirstCategory() !== prevFirstCategory) {
        setCategoriesChanged(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmojiGroups, loading, query, emojis]);
  var findCategoryToActivate = function findCategoryToActivate(row) {
    var category = null;
    if (row instanceof CategoryHeadingItem) {
      category = row.props.id;
    } else if (row instanceof EmojisRowItem) {
      category = row.props.category;
    }
    // your uploads is rendered, take it as upload category, so could be highlighted in category selector
    if (category === yourUploadsCategory) {
      return customCategory;
      // search results is rendered, return null so won't be highlighted for category selector
    } else if (category === searchCategory) {
      return null;
    }
    return category;
  };
  var rowSize = function rowSize(index) {
    var _virtualItems$index;
    return ((_virtualItems$index = virtualItems[index]) === null || _virtualItems$index === void 0 ? void 0 : _virtualItems$index.height) || sizes.categoryHeadingHeight;
  };
  var renderRow = function renderRow(context) {
    return virtualItemRenderer(virtualItems, context);
  };

  /**
   * After deleting emoji, we'll update the focus index to the first emoji of last row of your uploads, so when user navigate back focus will still work
   * if last emoji in your uploads is deleted, the updated focus index will be outdated, as there will be no your uploads section
   * however, it'll trigger onChange from VirtualList, which will update focus index automatically for us
   */
  var handleOnCloseDelete = function handleOnCloseDelete() {
    var list = listRef.current;
    list === null || list === void 0 || list.updateFocusIndex(lastYourUploadsRow);
    onCloseDelete();
  };
  var findEmojiRowAndColumnById = function findEmojiRowAndColumnById(emojiId) {
    var columnIndex = -1;
    // for most of cases, it'd be in first emoji row, so should be quite fast to find in real world
    var rowIndex = virtualItems.findIndex(function (rowItem) {
      if (rowItem instanceof EmojisRowItem) {
        // find uploaded emoji in each emoji row
        columnIndex = rowItem.props.emojis.findIndex(function (emoji) {
          return emoji.id === emojiId;
        });
        return columnIndex !== -1;
      }
      return false;
    });
    return {
      rowIndex: rowIndex,
      columnIndex: columnIndex
    };
  };

  /**
   * Scrolls to a category in the list view
   */
  useImperativeHandle(ref, function () {
    return {
      reveal: function reveal(category) {
        var row = categoryTracker.getRow(category);
        _scrollToRow(listRef, row);
      },
      scrollToBottom: function scrollToBottom() {
        _scrollToRow(listRef, virtualItems.length);
      },
      scrollToTop: function scrollToTop() {
        _scrollToRow(listRef, 0);
      },
      scrollToRow: function scrollToRow(index) {
        _scrollToRow(listRef, index);
      },
      scrollToRecentlyUploaded: function scrollToRecentlyUploaded(uploadedEmoji) {
        // when search results is shown
        if (query) {
          var _findEmojiRowAndColum = findEmojiRowAndColumnById(uploadedEmoji.id),
            rowIndex = _findEmojiRowAndColum.rowIndex,
            columnIndex = _findEmojiRowAndColum.columnIndex;
          if (rowIndex !== -1) {
            var _listRef$current;
            (_listRef$current = listRef.current) === null || _listRef$current === void 0 || _listRef$current.scrollToEmojiAndFocus(rowIndex, columnIndex);
          }
        } else {
          // when seeing all emojis
          var row = lastYourUploadsRow;
          if (row > 0) {
            var _listRef$current2;
            (_listRef$current2 = listRef.current) === null || _listRef$current2 === void 0 || _listRef$current2.scrollToRowAndFocusLastEmoji(lastYourUploadsRow);
          }
        }
      }
    };
  });
  useEffect(function () {
    if (!query) {
      buildEmojiGroupedByCategory(emojis, currentUser);
    }
  }, [emojis, selectedTone, loading, query, currentUser, buildEmojiGroupedByCategory]);
  useEffect(function () {
    buildVirtualItems();
  }, [allEmojiGroups, buildVirtualItems]);
  useEffect(function () {
    if (categoriesChanged) {
      onRowsRendered({
        startIndex: 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualItems, categoriesChanged]);
  var virtualListHeight = useMemo(function () {
    return sizes.listHeight + emojiPickerHeightOffset(size);
  }, [size]);
  return /*#__PURE__*/React.createElement(EmojiPickerTabPanel, {
    showSearchResults: !!query
  }, /*#__PURE__*/React.createElement(EmojiActions, {
    selectedTone: selectedTone,
    onToneSelected: onToneSelected,
    onToneSelectorCancelled: onToneSelectorCancelled,
    toneEmoji: toneEmoji,
    uploading: uploading,
    uploadEnabled: uploadEnabled,
    emojiToDelete: emojiToDelete,
    initialUploadName: initialUploadName,
    uploadErrorMessage: uploadErrorMessage,
    onUploadCancelled: onUploadCancelled,
    onUploadEmoji: onUploadEmoji,
    onCloseDelete: handleOnCloseDelete,
    onDeleteEmoji: onDeleteEmoji,
    onFileChooserClicked: onFileChooserClicked,
    onOpenUpload: onOpenUpload,
    query: query,
    onChange: onSearch,
    resultsCount: emojis.length
  }), /*#__PURE__*/React.createElement(EmojiPickerListContextProvider, {
    initialEmojisFocus: {
      rowIndex: 1,
      columnIndex: 0
    }
  }, /*#__PURE__*/React.createElement(VirtualList, {
    ref: listRef,
    height: virtualListHeight,
    overscanRowCount: 10,
    rowCount: virtualItems.length,
    rowHeight: rowSize,
    rowRenderer: renderRow,
    scrollToAlignment: "start",
    width: sizes.listWidth,
    onRowsRendered: onRowsRendered
  })));
});